<template>
  <section class="bg-gray-100 rounded-lg p-4 sm:p-6">
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
      <h2 class="text-xl font-semibold text-gray-800">Lista de Noticias</h2>
      <button
        type="button"
        class="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 self-start sm:self-auto"
        @click="openForm(null)"
      >
        + Nueva Noticia
      </button>
    </div>

    <div class="flex flex-wrap gap-3 mb-4">
      <select
        v-model="filters.status"
        class="border-gray-200 rounded-md text-sm focus:border-indigo-600 focus:ring-indigo-500"
        aria-label="Filtrar por estado"
        @change="load"
      >
        <option value="">Todos los estados</option>
        <option v-for="s in statuses" :key="s.value" :value="s.value">{{ s.label }}</option>
      </select>
      <select
        v-model="filters.category_id"
        class="border-gray-200 rounded-md text-sm focus:border-indigo-600 focus:ring-indigo-500"
        aria-label="Filtrar por categoría"
        @change="load"
      >
        <option value="">Todas las categorías</option>
        <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
      </select>
    </div>

    <p v-if="error" class="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{{ error }}</p>

    <p v-if="loading" class="py-8 text-center text-sm text-gray-500">Cargando noticias…</p>
    <p v-else-if="!posts.length" class="py-8 text-center text-sm text-gray-500">No hay noticias.</p>

    <ul v-else class="space-y-4">
      <li v-for="post in posts" :key="post.id" class="bg-white border border-gray-200 rounded-lg p-4">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <h3 class="text-base font-medium text-gray-800 break-words">{{ post.title }}</h3>
              <span :class="['px-2 py-0.5 text-xs rounded', statusClass(post.status)]">
                {{ statusLabel(post.status) }}
              </span>
              <span v-if="post.category_name" class="px-2 py-0.5 text-xs rounded bg-indigo-100 text-indigo-800">
                {{ post.category_name }}
              </span>
            </div>
            <p v-if="post.excerpt" class="mt-1 text-sm text-gray-600">{{ post.excerpt }}</p>
            <p class="mt-1 text-xs text-gray-500 flex flex-wrap gap-x-4">
              <span v-if="post.published_at">Publicado: {{ formatDateTime(post.published_at) }}</span>
              <span>Creado: {{ formatDateTime(post.created_at) }}</span>
            </p>
          </div>
          <div class="flex items-center gap-3 shrink-0">
            <button type="button" class="text-blue-500 hover:text-blue-700" title="Editar" aria-label="Editar noticia" @click="openForm(post)">
              <i class="fa-regular fa-pen-to-square"></i>
            </button>
            <button type="button" class="text-red-500 hover:text-red-700" title="Eliminar" aria-label="Eliminar noticia" @click="askDeletePost(post)">
              <i class="fa-regular fa-trash-can"></i>
            </button>
          </div>
        </div>

        <!-- Imágenes -->
        <div class="flex flex-wrap gap-2 mt-3">
          <div v-for="img in sortedImages(post)" :key="img.id" class="relative w-20 h-20">
            <AuthImage
              :src="resolveImageUrl(img.formats && img.formats.thumb)"
              :alt="img.alt_text || post.title"
              class="w-20 h-20 object-cover rounded-md border border-gray-200"
            />
            <button
              type="button"
              class="absolute -bottom-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] hover:bg-red-700"
              title="Eliminar imagen"
              aria-label="Eliminar imagen"
              @click="askDeleteImage(post, img)"
            >
              <i class="fa-regular fa-trash-can"></i>
            </button>
          </div>
          <button
            v-if="(post.images || []).length < maxImages"
            type="button"
            class="w-20 h-20 flex items-center justify-center rounded-md border-2 border-dashed border-gray-300 text-gray-400 text-2xl hover:border-gray-400 hover:text-gray-500"
            title="Agregar imágenes"
            aria-label="Agregar imágenes"
            @click="imagesPost = post"
          >
            +
          </button>
        </div>
      </li>
    </ul>

    <NoticiaFormModal
      v-if="formOpen"
      :post="editingPost"
      :categories="categories"
      @close="formOpen = false"
      @saved="onSaved"
    />

    <NoticiaImagesModal
      v-if="imagesPost"
      :post="imagesPost"
      @close="imagesPost = null"
      @saved="onImagesSaved"
    />

    <ConfirmAction
      :is-visible="!!pendingDelete"
      :title="confirmTitle"
      :description="confirmDescription"
      action="delete"
      :processing="deleting"
      @confirm="confirmDelete"
      @cancel="pendingDelete = null"
    />
  </section>
</template>

<script>
import AuthImage from '@/components/commons/AuthImage.vue';
import NoticiaFormModal from './NoticiaFormModal.vue';
import NoticiaImagesModal from './NoticiaImagesModal.vue';
import { blogCategoriesApi, blogPostImagesApi, blogPostsApi } from '@/service';
import {
  BLOG_STATUSES,
  MAX_IMAGES_PER_POST,
  apiErrorMessage,
  blogStatusClass,
  blogStatusLabel,
  formatDateTime,
  resolveImageUrl,
} from '@/utils/blog';

export default {
  name: 'NoticiasList',
  components: { AuthImage, NoticiaFormModal, NoticiaImagesModal },
  data() {
    return {
      posts: [],
      categories: [],
      filters: { status: '', category_id: '' },
      loading: false,
      error: '',
      statuses: BLOG_STATUSES,
      maxImages: MAX_IMAGES_PER_POST,
      formOpen: false,
      editingPost: null,
      imagesPost: null,
      // { type: 'post' | 'image', post, image? }
      pendingDelete: null,
      deleting: false,
    };
  },
  computed: {
    confirmTitle() {
      return this.pendingDelete?.type === 'image' ? 'Eliminar imagen' : 'Eliminar noticia';
    },
    confirmDescription() {
      if (!this.pendingDelete) return '';
      return this.pendingDelete.type === 'image'
        ? `¿Eliminar esta imagen de "${this.pendingDelete.post.title}"?`
        : `¿Eliminar "${this.pendingDelete.post.title}" y todas sus imágenes? Esta acción no se puede deshacer.`;
    },
  },
  created() {
    this.loadCategories();
    this.load();
  },
  methods: {
    statusLabel: blogStatusLabel,
    statusClass: blogStatusClass,
    formatDateTime,
    resolveImageUrl,
    sortedImages(post) {
      return [...(post.images || [])].sort((a, b) => a.position - b.position);
    },
    async loadCategories() {
      try {
        const { data } = await blogCategoriesApi.list();
        this.categories = data.data || [];
      } catch (e) {
        this.error = apiErrorMessage(e, 'No se pudieron cargar las categorías.');
      }
    },
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await blogPostsApi.list({ ...this.filters, include_images: true });
        this.posts = data.data || [];
      } catch (e) {
        this.error = apiErrorMessage(e, 'No se pudieron cargar las noticias.');
      } finally {
        this.loading = false;
      }
    },
    openForm(post) {
      this.editingPost = post;
      this.formOpen = true;
    },
    onSaved() {
      this.formOpen = false;
      this.load();
    },
    onImagesSaved() {
      this.imagesPost = null;
      this.load();
    },
    askDeletePost(post) {
      this.pendingDelete = { type: 'post', post };
    },
    askDeleteImage(post, image) {
      this.pendingDelete = { type: 'image', post, image };
    },
    async confirmDelete() {
      const { type, post, image } = this.pendingDelete;
      this.deleting = true;
      try {
        if (type === 'image') {
          await blogPostImagesApi.remove(post.id, image.id);
        } else {
          await blogPostsApi.remove(post.id);
        }
        this.pendingDelete = null;
        await this.load();
      } catch (e) {
        this.pendingDelete = null;
        this.error = apiErrorMessage(e, 'No se pudo eliminar.');
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>
